import axios from "axios";
import endpoints from "hooks/api/endpoints";
import IndividualItemResponse from "types/IndividualItemResponse";
import VideoSubtitleDto, {
  VideoSubtitleBlobUrls,
} from "types/VideoSubtitleDto";

const ENGLISH_SUBTITLE_LANGUAGE = "en-US";

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) {
    return Promise.reject(new Error("aborted"));
  }
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      globalThis.clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      reject(new Error("aborted"));
    };
    const timer = globalThis.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort);
  });
}

export async function getVideoSubtitles(
  projectId: string,
  videoId: string,
): Promise<VideoSubtitleDto> {
  const res = await axios.get<IndividualItemResponse<VideoSubtitleDto>>(
    endpoints.getVideoSubtitles(projectId, videoId),
  );
  return res.data.data;
}

export async function ensureVideoSubtitles(
  projectId: string,
  videoId: string,
): Promise<VideoSubtitleDto> {
  const res = await axios.post<IndividualItemResponse<VideoSubtitleDto>>(
    endpoints.postVideoSubtitlesEnsure(projectId, videoId),
  );
  return res.data.data;
}

/** Loads VTT via API (Bearer) and returns a blob URL for `<track src>`. */
export async function fetchVideoSubtitlesBlobUrl(
  projectId: string,
  videoId: string,
  signal?: AbortSignal,
  languageCode?: string,
): Promise<string> {
  const res = await axios.get(
    endpoints.getVideoSubtitlesFile(projectId, videoId, languageCode),
    {
      responseType: "blob",
      signal,
    },
  );
  return URL.createObjectURL(res.data);
}

function needsEnglishTrack(englishStatus?: VideoSubtitleDto["englishStatus"]) {
  return englishStatus === "READY" || englishStatus === "PENDING";
}

function sourceSubtitlesReady(data: VideoSubtitleDto) {
  return data.status === "READY";
}

function englishSubtitlesReady(data: VideoSubtitleDto) {
  return (
    data.englishStatus === "READY" || data.englishStatus === "NOT_APPLICABLE"
  );
}

/**
 * Ensures subtitles exist, polls while pending, then returns blob URLs for source
 * (and English when applicable).
 */
export async function ensureVideoSubtitlesWithPoll(
  projectId: string,
  videoId: string,
  signal?: AbortSignal,
): Promise<VideoSubtitleBlobUrls> {
  let data = await ensureVideoSubtitles(projectId, videoId);

  const waitForReady = async (): Promise<VideoSubtitleDto> => {
    if (sourceSubtitlesReady(data) && englishSubtitlesReady(data)) {
      return data;
    }
    for (let i = 0; i < 40; i++) {
      if (signal?.aborted) {
        throw new Error("aborted");
      }
      await sleep(3000, signal);
      data = await getVideoSubtitles(projectId, videoId);
      if (
        !sourceSubtitlesReady(data) ||
        (needsEnglishTrack(data.englishStatus) &&
          data.englishStatus === "PENDING")
      ) {
        data = await ensureVideoSubtitles(projectId, videoId);
      }
      if (sourceSubtitlesReady(data) && englishSubtitlesReady(data)) {
        return data;
      }
    }
    throw new Error("Subtitles are still processing. Try again later.");
  };

  const ready = await waitForReady();

  const sourceUrl = await fetchVideoSubtitlesBlobUrl(
    projectId,
    videoId,
    signal,
  );

  if (!needsEnglishTrack(ready.englishStatus)) {
    return { sourceUrl };
  }

  if (ready.englishStatus !== "READY") {
    throw new Error("English subtitles are still processing. Try again later.");
  }

  const englishUrl = await fetchVideoSubtitlesBlobUrl(
    projectId,
    videoId,
    signal,
    ENGLISH_SUBTITLE_LANGUAGE,
  );

  return { sourceUrl, englishUrl };
}
